import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Form } from 'react-final-form';
import { Button, ButtonWithLoading } from '@spark/ui-components/components/Button';
import TanStackTable from '@spark/ui-components/components/TanStackTable/TanStackTable';
import FilterPanel from '@spark/ui-components/components/FilterPanel';
import CollapsibleCard from '@spark/ui-components/components/CollapsibleCard';
import { FormButtonToolbar, FormContainer } from '@spark/ui-components/components/form/FormElements';
import DynamicForm from '@spark/ui-components/components/form/DynamicForm';
import { notification } from '@spark/ui-components/utils/notification';
import {
  getPremiacaoTabelaVeiculoById,
  createPremiacaoTabelaVeiculo,
  updatePremiacaoTabelaVeiculo,
  deletePremiacaoTabelaVeiculo
} from '@spark/services/premiacao';
import { tabelaVeiculoFilterConfig } from '../config/tabelaVeiculoFilterConfig';
import { getTabelaVeiculoColumns } from '../config/tabelaVeiculoColumns';
import {
  emptyTabelaVeiculoForm,
  tabelaVeiculoFormSections,
  tabelaVeiculoFormStructure,
  validateTabelaVeiculoForm
} from '../config/tabelaVeiculoFormConfig';
import { usePremiacaoTabelaVeiculoSelection } from '../hooks/usePremiacaoTabelaVeiculoSelection';
import PremiacaoImportacaoExcelModal from './PremiacaoImportacaoExcelModal';
import { importarPremiacaoTabelaVeiculos, exportarLayoutImportacaoPremiacaoTabelaVeiculosComRegistros } from '@spark/services/premiacao';
import { LAYOUT_IMPORTACAO_RELACIONAMENTOS } from '../config/premiacaoImportLayouts';

const pick = (data, ...keys) => {
  for (let index = 0; index < keys.length; index += 1) {
    const key = keys[index];
    if (data?.[key] !== undefined && data?.[key] !== null) {
      return data[key];
    }
  }
  return undefined;
};

const extractValue = (value) => {
  if (value === null || value === undefined) return value;
  if (typeof value === 'object' && value.value !== undefined) return value.value;
  return value;
};

const parseNumber = (value) => {
  const rawValue = extractValue(value);
  if (rawValue === '' || rawValue === null || rawValue === undefined) return null;
  const parsed = Number(rawValue);
  return Number.isNaN(parsed) ? null : parsed;
};

const normalizeText = (value) => {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed.length > 0 ? trimmed : null;
};

const toRowModel = (item) => ({
  id: pick(item, 'id', 'Id'),
  numeroFrota: pick(item, 'numeroFrota', 'NumeroFrota') || '',
  idTabelaPremiacao: pick(item, 'idTabelaPremiacao', 'IdTabelaPremiacao'),
  nomeTabelaPremiacao: pick(item, 'nomeTabelaPremiacao', 'NomeTabelaPremiacao') || '',
  ativo: pick(item, 'ativo', 'Ativo') === true,
  data: pick(item, 'data', 'Data')
});

const mapApiToFormValues = (data) => ({
  id: pick(data, 'id', 'Id') ?? '',
  numeroFrota: pick(data, 'numeroFrota', 'NumeroFrota') || '',
  idTabelaPremiacao: pick(data, 'idTabelaPremiacao', 'IdTabelaPremiacao') ?? '',
  ativo: pick(data, 'ativo', 'Ativo') === true
});

const buildPayload = (values) => ({
  NumeroFrota: normalizeText(values.numeroFrota) ?? '',
  IdTabelaPremiacao: parseNumber(values.idTabelaPremiacao) ?? 0,
  Ativo: values.ativo === true
});

const PremiacaoTabelaVeiculoModal = ({ show, onHide }) => {
  const [editingId, setEditingId] = useState(null);
  const [formInitialValues, setFormInitialValues] = useState(emptyTabelaVeiculoForm);
  const [saving, setSaving] = useState(false);
  const [loadingEditId, setLoadingEditId] = useState(null);
  const [loadingDeleteId, setLoadingDeleteId] = useState(null);
  const [isFormCollapsed, setIsFormCollapsed] = useState(true);
  const [showImportacaoModal, setShowImportacaoModal] = useState(false);
  const {
    registros,
    loading,
    paginaAtual,
    quantidadePorPagina,
    totalRegistros,
    handleFiltrosChange,
    handlePaginationChange,
    reload
  } = usePremiacaoTabelaVeiculoSelection(show);

  useEffect(() => {
    if (!show) {
      setEditingId(null);
      setFormInitialValues(emptyTabelaVeiculoForm);
      setLoadingEditId(null);
      setLoadingDeleteId(null);
      setIsFormCollapsed(true);
      setShowImportacaoModal(false);
    }
  }, [show]);

  const handleEdit = useCallback(async (id) => {
    if (!id || loadingEditId === id) return;

    try {
      setLoadingEditId(id);
      const data = await getPremiacaoTabelaVeiculoById(id);
      setEditingId(id);
      setFormInitialValues(mapApiToFormValues(data));
      setIsFormCollapsed(false);
    } catch (error) {
      notification.error('Erro ao carregar associacao frota/tabela');
      console.error('Erro ao carregar associacao frota/tabela:', error);
    } finally {
      setLoadingEditId(null);
    }
  }, [loadingEditId]);

  const handleDelete = useCallback(async (id) => {
    if (!id || loadingDeleteId === id) return;

    try {
      setLoadingDeleteId(id);
      await deletePremiacaoTabelaVeiculo(id);
      notification.success('Associacao excluida com sucesso!');
      if (editingId === id) {
        setEditingId(null);
        setFormInitialValues(emptyTabelaVeiculoForm);
      }
      reload();
    } catch (error) {
      notification.error('Erro ao excluir associacao');
      console.error('Erro ao excluir associacao:', error);
    } finally {
      setLoadingDeleteId(null);
    }
  }, [editingId, loadingDeleteId, reload]);

  const handleClear = useCallback((form) => {
    setEditingId(null);
    setFormInitialValues(emptyTabelaVeiculoForm);
    form.reset(emptyTabelaVeiculoForm);
    setIsFormCollapsed(false);
  }, []);

  const handleSubmit = useCallback(async (values) => {
    const payload = buildPayload(values);

    try {
      setSaving(true);
      let data;

      if (values.id) {
        data = await updatePremiacaoTabelaVeiculo(values.id, payload);
        notification.success('Associacao atualizada com sucesso!');
      } else {
        data = await createPremiacaoTabelaVeiculo(payload);
        notification.success('Associacao criada com sucesso!');
      }

      setEditingId(pick(data, 'id', 'Id') || values.id || null);
      setFormInitialValues(mapApiToFormValues(data));
      reload();
      setIsFormCollapsed(false);
    } catch (error) {
      notification.error('Erro ao salvar associacao');
      console.error('Erro ao salvar associacao:', error);
    } finally {
      setSaving(false);
    }
  }, [reload]);

  const rows = Array.isArray(registros) ? registros.map(toRowModel) : [];
  const formKey = useMemo(() => (editingId ? `edit-${editingId}` : 'create'), [editingId]);

  return (
    <>
    <Modal show={show} onHide={onHide} animation={false} size="xl" dialogClassName="modal-uniform-xl">
      <Modal.Header closeButton>
        <Modal.Title>Frotas × Tabela de Premiação</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <FilterPanel
          filters={tabelaVeiculoFilterConfig}
          onApply={handleFiltrosChange}
          title="Filtros de Busca"
          loading={loading}
          defaultCollapsed={false}
        />

        <TanStackTable
          key={`premiacao-tabela-veiculo-${paginaAtual}-${quantidadePorPagina}`}
          columns={getTabelaVeiculoColumns(handleEdit, handleDelete, loadingEditId, loadingDeleteId)}
          data={rows}
          loading={loading}
          withPagination={true}
          pageSizeOptions={[5, 10, 25, 50]}
          enableRowColors={true}
          className="mt-3"
          manualPagination={true}
          totalItems={totalRegistros}
          controlledPagination={{
            pageIndex: Math.max(0, (paginaAtual || 1) - 1),
            pageSize: quantidadePorPagina || 10
          }}
          onPaginationChange={handlePaginationChange}
          enableExportExcel={true}
          exportFileName="premiacao-frotas-tabela"
        />

        <CollapsibleCard
          className="mt-3"
          title="Formulário de Associação"
          isCollapsed={isFormCollapsed}
          onToggle={setIsFormCollapsed}
        >
          <Form
            key={formKey}
            onSubmit={handleSubmit}
            initialValues={formInitialValues}
            validate={validateTabelaVeiculoForm}
          >
            {({ handleSubmit: onFormSubmit, form }) => (
              <FormContainer onSubmit={onFormSubmit}>
                <DynamicForm
                  structure={tabelaVeiculoFormStructure}
                  sections={tabelaVeiculoFormSections()}
                  readOnly={false}
                />

                <FormButtonToolbar className="mt-3">
                  <Button variant="secondary" type="button" onClick={() => handleClear(form)} disabled={saving}>
                    Limpar
                  </Button>
                  <ButtonWithLoading variant="primary" type="submit" loading={saving}>
                    Salvar
                  </ButtonWithLoading>
                </FormButtonToolbar>
              </FormContainer>
            )}
          </Form>
        </CollapsibleCard>
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-between w-100">
          <div className="text-muted">
            {totalRegistros > 0 && (
              <span>
                Exibindo {rows.length} de {totalRegistros} associacao(oes)
              </span>
            )}
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-primary" onClick={() => setShowImportacaoModal(true)}>
              Importar Relacionamentos
            </Button>
            <Button variant="secondary" onClick={onHide}>
              Fechar
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>

    <PremiacaoImportacaoExcelModal
      show={showImportacaoModal}
      onClose={() => setShowImportacaoModal(false)}
      onImportado={reload}
      title="Importar Relacionamentos Frota x Tabela"
      layoutColumns={LAYOUT_IMPORTACAO_RELACIONAMENTOS.columns}
      layoutObservacoes={LAYOUT_IMPORTACAO_RELACIONAMENTOS.observacoes}
      layoutFileName={LAYOUT_IMPORTACAO_RELACIONAMENTOS.fileName}
      layoutFileNameComRegistros={LAYOUT_IMPORTACAO_RELACIONAMENTOS.fileNameComRegistros}
      onImport={importarPremiacaoTabelaVeiculos}
      onDownloadLayoutComRegistros={exportarLayoutImportacaoPremiacaoTabelaVeiculosComRegistros}
    />
    </>
  );
};

export default PremiacaoTabelaVeiculoModal;
